/**
 * Period control: presets first, custom dates behind a tap.
 *
 * Two date inputs are the honest general answer and the wrong default — nobody
 * opens a sales register wanting to type two dates. "Last month" is one tap and
 * covers most of what gets asked.
 */
import { useState } from "react";

import { inputClass } from "./ui.js";
import { presets } from "../period.js";
import type { Period } from "../period.js";

export function PeriodPicker({
  today,
  value,
  onChange,
}: {
  today: string;
  value: Period;
  onChange: (period: Period) => void;
}) {
  const [custom, setCustom] = useState(value.id === "custom");
  const options = presets(today);

  return (
    <div className="space-y-2">
      <div className="no-bar -mx-4 flex gap-2 overflow-x-auto px-4">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              setCustom(false);
              onChange(option);
            }}
            className={
              "shrink-0 rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors " +
              (value.id === option.id
                ? "bg-accent text-accent-ink"
                : "border border-line bg-surface text-ink-2")
            }
          >
            {option.label}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setCustom((on) => !on)}
          className={
            "shrink-0 rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors " +
            (value.id === "custom"
              ? "bg-accent text-accent-ink"
              : "border border-line bg-surface text-ink-2")
          }
        >
          Dates…
        </button>
      </div>

      {custom && (
        <div className="grid grid-cols-2 gap-3">
          <input
            type="date"
            className={inputClass}
            value={value.from}
            onChange={(e) =>
              onChange({ ...value, id: "custom", label: "Custom", from: e.target.value })
            }
          />
          <input
            type="date"
            className={inputClass}
            value={value.to}
            onChange={(e) =>
              onChange({ ...value, id: "custom", label: "Custom", to: e.target.value })
            }
          />
        </div>
      )}
    </div>
  );
}

/**
 * A change against the same window last year.
 *
 * Renders nothing when the base is zero: "+∞%" against a month with no sales is
 * noise, and the absolute figures beside it already tell the story.
 */
export function Delta({ percent }: { percent: number | null }) {
  if (percent === null) return null;

  const up = percent >= 0;
  return (
    <span className={`text-xs font-medium ${up ? "text-good" : "text-bad"}`}>
      {up ? "▲" : "▼"} {Math.abs(percent).toFixed(0)}%
    </span>
  );
}
